// Chemin: C:\gestion-copro\src\pages\public\InscriptionVigiePage.tsx
// Page d'inscription autonome pour la formule VIGIE
// Session 101 : Création complète — Phase 2 chantier ESCALATOR
// Session 139 : Intégration Stripe — choix mensuel/annuel + redirection Checkout
// Session 140 : Gestion email-already-in-use — reconnexion + relance Stripe si en_attente_paiement
// Session 147 : Prix fondateur (5,90€/59€) + compteur places + typeOffre + quota 100 analyses
// Session 163 : REFONTE — inscription_pending au lieu de compte Auth
//   Le compte Auth est créé après paiement (webhook Stripe), pas avant.
//   Élimine les comptes fantômes et les problèmes "email déjà utilisé".

import React, { useState, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, EyeOff, Building2, User, Mail, Lock, MapPin, Info, CreditCard, Check, Loader2, Star, Users } from 'lucide-react';
import { registerVigie } from '../../services/inscription.service';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

const MAX_PLACES_FONDATEUR = 20;

const InscriptionVigiePage: React.FC = () => {
  // État du formulaire
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    password: '',
    passwordConfirm: '',
    nomCopropriete: '',
    adresseCopropriete: '',
    nombreLots: '',
    nomSyndic: '',
    acceptCGU: false,
  });

  // Choix de la formule
  const [formule, setFormule] = useState<'annuel' | 'mensuel'>('annuel');

  // Places fondateur
  const [placesRestantes, setPlacesRestantes] = useState<number | null>(null);
  const [offreFondateurDispo, setOffreFondateurDispo] = useState(true);

  // États UI
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [etape, setEtape] = useState<'formulaire' | 'redirection'>('formulaire');

  // ============================================================
  // Charger le compteur de places fondateur
  // ============================================================
  useEffect(() => {
    const chargerPlacesFondateur = async () => {
      try {
        const q = query(
          collection(db, 'users'),
          where('typeOffre', '==', 'fondateur'),
          where('statutAbonnement', 'in', ['actif', 'en_attente_paiement'])
        );
        const snap = await getDocs(q);
        const restantes = MAX_PLACES_FONDATEUR - snap.size;
        setPlacesRestantes(Math.max(0, restantes));
        setOffreFondateurDispo(restantes > 0);
      } catch (err) {
        console.error('Erreur chargement places fondateur:', err);
        // En cas d'erreur, on garde l'offre dispo par défaut
        setPlacesRestantes(null);
        setOffreFondateurDispo(true);
      }
    };
    chargerPlacesFondateur();
  }, []);

  // Gestion des champs
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (error) setError(null);
  };

  // Validation du formulaire
  const validateForm = (): string | null => {
    if (!formData.prenom.trim()) return 'Le prénom est requis';
    if (!formData.nom.trim()) return 'Le nom est requis';
    if (!formData.email.trim()) return 'L\'adresse email est requise';
    if (!formData.password) return 'Le mot de passe est requis';
    if (formData.password.length < 6) return 'Le mot de passe doit contenir au moins 6 caractères';
    if (formData.password !== formData.passwordConfirm) return 'Les mots de passe ne correspondent pas';
    if (!formData.nomCopropriete.trim()) return 'Le nom de votre copropriété est requis';
    if (!formData.adresseCopropriete.trim()) return 'L\'adresse de votre copropriété est requise';
    if (!formData.acceptCGU) return 'Vous devez accepter les conditions générales d\'utilisation';
    return null;
  };

  // ============================================================
  // Session 163 : Soumission simplifiée
  // 1. Stocke dans inscriptions_pending (pas de compte Auth)
  // 2. Redirige vers Stripe Checkout
  // ============================================================
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Étape 1 : Stocker les données dans inscriptions_pending
      const result = await registerVigie({
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        nom: formData.nom.trim(),
        prenom: formData.prenom.trim(),
        nomCopropriete: formData.nomCopropriete.trim(),
        adresseCopropriete: formData.adresseCopropriete.trim(),
        nombreLots: formData.nombreLots ? parseInt(formData.nombreLots, 10) : undefined,
        nomSyndic: formData.nomSyndic.trim() || undefined,
      });

      if (!result.success || !result.inscriptionId) {
        setError(result.error || 'Une erreur est survenue lors de l\'inscription');
        setIsSubmitting(false);
        return;
      }

      // Étape 2 : Créer la session Stripe Checkout
      setEtape('redirection');

      const functions = getFunctions(undefined, 'europe-west1');
      const createCheckoutSession = httpsCallable<
        { formule: string; inscriptionId: string; typeOffre: string },
        { sessionId: string; url: string }
      >(functions, 'createCheckoutSession');

      const stripeResult = await createCheckoutSession({
        formule,
        inscriptionId: result.inscriptionId,
        typeOffre: offreFondateurDispo ? 'fondateur' : 'normal',
      });

      // Étape 3 : Rediriger vers Stripe Checkout
      if (stripeResult.data.url) {
        window.location.href = stripeResult.data.url;
      } else {
        setError('Impossible de créer la session de paiement. Veuillez réessayer.');
        setEtape('formulaire');
        setIsSubmitting(false);
      }

    } catch (err: any) {
      console.error('Erreur inscription vigie:', err);

      // Gestion spécifique places fondateur épuisées
      if (err?.code === 'functions/resource-exhausted') {
        setOffreFondateurDispo(false);
        setPlacesRestantes(0);
        setError('Les places fondateur sont épuisées. Rechargez la page pour voir les tarifs actuels.');
      } else {
        setError('Une erreur inattendue est survenue. Veuillez réessayer.');
      }

      setEtape('formulaire');
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // Écran de redirection vers Stripe
  // ============================================================
  if (etape === 'redirection') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Loader2 className="text-amber-600 animate-spin" size={32} />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Redirection vers le paiement...
          </h2>
          <p className="text-gray-600 mb-4">
            Vous allez être redirigé vers la page de paiement sécurisée Stripe.
          </p>
          <p className="text-sm text-gray-400">
            Votre compte sera créé automatiquement après le paiement.
          </p>
        </div>
      </div>
    );
  }

  // ============================================================
  // Prix affichés selon disponibilité offre fondateur
  // ============================================================
  const prixAnnuel = offreFondateurDispo ? '59€' : '149€';
  const prixMensuel = offreFondateurDispo ? '5,90€' : '14,90€';
  const prixMensuelEquiv = offreFondateurDispo ? '4,92€' : '12,42€';
  const prixAnnuelHT = offreFondateurDispo ? '49,17€' : '124,17€';
  const prixMensuelHT = offreFondateurDispo ? '4,92€' : '12,42€';
  const economie = offreFondateurDispo ? '2 mois offerts' : '2 mois offerts';

  // ============================================================
  // Formulaire d'inscription
  // ============================================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link
            to="/"
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors inline-flex items-center gap-1"
          >
            <ArrowLeft size={14} />
            Retour à l'accueil
          </Link>
          <img 
            src="/logo-copro-protect.png" 
            alt="Copro Protect" 
            className="h-10"
          />
        </div>
      </header>

      {/* Contenu principal */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Titre et description */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-4">
            <Shield size={16} />
            Formule VIGIE
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Créez votre espace personnel
          </h1>
          <p className="text-gray-500">
            Protégez votre investissement immobilier avec l'IA, même sans conseil syndical.
          </p>
        </div>

        {/* ===== Bandeau offre fondateur ===== */}
        {offreFondateurDispo && (
          <div className="mb-6 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Star size={20} />
              </div>
              <div className="flex-1">
                <p className="font-bold text-sm">Offre Fondateur — Tarif verrouillé à vie</p>
                <p className="text-white/90 text-xs mt-0.5">
                  Rejoignez les premiers utilisateurs et bénéficiez d'un tarif préférentiel permanent.
                </p>
              </div>
              {placesRestantes !== null && (
                <div className="text-right flex-shrink-0">
                  <div className="flex items-center gap-1.5 bg-white/20 rounded-lg px-3 py-1.5">
                    <Users size={14} />
                    <span className="font-bold text-sm">{placesRestantes}/{MAX_PLACES_FONDATEUR}</span>
                  </div>
                  <p className="text-white/80 text-xs mt-1">
                    {placesRestantes <= 5 ? 'Dernières places !' : 'Places restantes'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== Choix de la formule ===== */}
        <div className="mb-8">
          <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard size={18} className="text-amber-600" />
            Choisissez votre formule
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Formule annuelle */}
            <button
              type="button"
              onClick={() => setFormule('annuel')}
              className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                formule === 'annuel'
                  ? 'border-amber-500 bg-amber-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              {formule === 'annuel' && (
                <div className="absolute top-3 right-3 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                  <Check size={14} className="text-white" />
                </div>
              )}
              <div className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded mb-2">
                {economie}
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {prixAnnuel}<span className="text-sm font-normal text-gray-500"> TTC/an</span>
              </div>
              <div className="text-sm text-gray-500 mt-1">soit {prixMensuelEquiv}/mois</div>
              <div className="text-xs text-gray-400 mt-0.5">{prixAnnuelHT} HT/an</div>
              {offreFondateurDispo && (
                <div className="mt-2 text-xs text-amber-700 font-medium flex items-center gap-1">
                  <Star size={12} />
                  Prix fondateur
                  <span className="line-through text-gray-400 ml-1">149€</span>
                </div>
              )}
            </button>

            {/* Formule mensuelle */}
            <button
              type="button"
              onClick={() => setFormule('mensuel')}
              className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                formule === 'mensuel'
                  ? 'border-amber-500 bg-amber-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              {formule === 'mensuel' && (
                <div className="absolute top-3 right-3 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                  <Check size={14} className="text-white" />
                </div>
              )}
              <div className="text-2xl font-bold text-gray-900 mt-5">
                {prixMensuel}<span className="text-sm font-normal text-gray-500"> TTC/mois</span>
              </div>
              <div className="text-sm text-gray-500 mt-1">sans engagement</div>
              <div className="text-xs text-gray-400 mt-0.5">{prixMensuelHT} HT/mois</div>
              {offreFondateurDispo && (
                <div className="mt-2 text-xs text-amber-700 font-medium flex items-center gap-1">
                  <Star size={12} />
                  Prix fondateur
                  <span className="line-through text-gray-400 ml-1">14,90€</span>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Encadré avantages */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <Info className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">Votre formule VIGIE inclut :</p>
              <p>
                SENTINELLE (assistant IA juridique), analyse de documents, génération de courriers professionnels,
                bible juridique copropriété — 100 analyses IA/an et 2 Go de stockage. Activation immédiate après paiement.
              </p>
            </div>
          </div>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
          
          {/* Message d'erreur */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* ===== Section : Vos informations ===== */}
          <div className="mb-8">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User size={18} className="text-amber-600" />
              Vos informations
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Prénom */}
              <div>
                <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-1">
                  Prénom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="prenom"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                  placeholder="Votre prénom"
                  required
                  disabled={isSubmitting}
                  autoComplete="given-name"
                  autoFocus
                />
              </div>

              {/* Nom */}
              <div>
                <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-1">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                  placeholder="Votre nom"
                  required
                  disabled={isSubmitting}
                  autoComplete="family-name"
                />
              </div>
            </div>

            {/* Email */}
            <div className="mt-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                <Mail size={14} className="inline mr-1 text-gray-400" />
                Adresse email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                placeholder="votre@email.fr"
                required
                disabled={isSubmitting}
                autoComplete="email"
              />
            </div>

            {/* Mot de passe */}
            <div className="mt-4">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                <Lock size={14} className="inline mr-1 text-gray-400" />
                Mot de passe <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors pr-12"
                  placeholder="Minimum 6 caractères"
                  required
                  disabled={isSubmitting}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Confirmation mot de passe */}
            <div className="mt-4">
              <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700 mb-1">
                <Lock size={14} className="inline mr-1 text-gray-400" />
                Confirmez le mot de passe <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPasswordConfirm ? 'text' : 'password'}
                  id="passwordConfirm"
                  name="passwordConfirm"
                  value={formData.passwordConfirm}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors pr-12"
                  placeholder="Retapez votre mot de passe"
                  required
                  disabled={isSubmitting}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPasswordConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {/* Indicateur de correspondance */}
              {formData.passwordConfirm && (
                <p className={`mt-1 text-xs ${formData.password === formData.passwordConfirm ? 'text-emerald-600' : 'text-red-500'}`}>
                  {formData.password === formData.passwordConfirm ? 'Les mots de passe correspondent' : 'Les mots de passe ne correspondent pas'}
                </p>
              )}
            </div>
          </div>

          {/* ===== Section : Votre copropriété ===== */}
          <div className="mb-8">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 size={18} className="text-amber-600" />
              Votre copropriété
            </h2>

            {/* Nom de la copropriété */}
            <div>
              <label htmlFor="nomCopropriete" className="block text-sm font-medium text-gray-700 mb-1">
                Nom de la copropriété <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="nomCopropriete"
                name="nomCopropriete"
                value={formData.nomCopropriete}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                placeholder="Ex : Résidence Les Oliviers"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Adresse */}
            <div className="mt-4">
              <label htmlFor="adresseCopropriete" className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin size={14} className="inline mr-1 text-gray-400" />
                Adresse de la copropriété <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="adresseCopropriete"
                name="adresseCopropriete"
                value={formData.adresseCopropriete}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                placeholder="Ex : 12 rue des Lilas, 75015 Paris"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              {/* Nombre de lots (optionnel) */}
              <div>
                <label htmlFor="nombreLots" className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de lots <span className="text-gray-400 text-xs font-normal">(optionnel)</span>
                </label>
                <input
                  type="number"
                  id="nombreLots"
                  name="nombreLots"
                  value={formData.nombreLots}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                  placeholder="Ex : 24"
                  min={1}
                  max={9999}
                  disabled={isSubmitting}
                />
              </div>

              {/* Nom du syndic (optionnel) */}
              <div>
                <label htmlFor="nomSyndic" className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du syndic <span className="text-gray-400 text-xs font-normal">(optionnel)</span>
                </label>
                <input
                  type="text"
                  id="nomSyndic"
                  name="nomSyndic"
                  value={formData.nomSyndic}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                  placeholder="Ex : Foncia, Nexity..."
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* ===== CGU ===== */}
          <div className="mb-8">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="acceptCGU"
                checked={formData.acceptCGU}
                onChange={handleChange}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                disabled={isSubmitting}
              />
              <span className="text-sm text-gray-600">
                J'accepte les{' '}
                <Link to="/cgu" target="_blank" className="text-amber-700 hover:text-amber-900 underline">
                  conditions générales d'utilisation
                </Link>
                {' '}et la{' '}
                <Link to="/politique-confidentialite" target="_blank" className="text-amber-700 hover:text-amber-900 underline">
                  politique de confidentialité
                </Link>
                {' '}<span className="text-red-500">*</span>
              </span>
            </label>
          </div>

          {/* ===== Récapitulatif + Bouton inscription ===== */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                Formule VIGIE{offreFondateurDispo ? ' Fondateur' : ''} — {formule === 'annuel' ? 'Annuelle' : 'Mensuelle'}
              </span>
              <span className="font-bold text-gray-900">
                {formule === 'annuel' ? `${prixAnnuel} TTC/an` : `${prixMensuel} TTC/mois`}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {formule === 'annuel' ? `Soit ${prixMensuelEquiv}/mois — ${economie}` : 'Sans engagement, résiliable à tout moment'}
            </p>
            {offreFondateurDispo && (
              <p className="text-xs text-amber-600 font-medium mt-1 flex items-center gap-1">
                <Star size={10} />
                Tarif fondateur verrouillé à vie
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !formData.acceptCGU}
            className={`
              w-full py-3 px-4 rounded-lg font-semibold text-white
              transition-all duration-200
              ${isSubmitting || !formData.acceptCGU
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-amber-600 hover:bg-amber-700 hover:shadow-lg'
              }
            `}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle 
                    className="opacity-25" 
                    cx="12" cy="12" r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                    fill="none"
                  />
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Création en cours...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <CreditCard size={18} />
                Créer mon compte et payer
              </span>
            )}
          </button>

          <p className="text-xs text-gray-400 text-center mt-3">
            Paiement sécurisé par Stripe. Vous serez redirigé vers une page de paiement sécurisée.
          </p>

          {/* Lien vers connexion */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-amber-700 hover:text-amber-900 font-medium">
                Se connecter
              </Link>
            </p>
          </div>
        </form>

        {/* Footer info */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-400">
            Copro Protect • DOM TECH & SERVICES • Paiement sécurisé par Stripe
          </p>
        </div>
      </main>
    </div>
  );
};

export default InscriptionVigiePage;
